"use client";
import { isTemporarySkipMapClickOrg } from "@gettactic/helpers/src/featureFlags";
import { IResource } from "@gettactic/api";
import { parseInteractiveZonesSvg } from "@gettactic/helpers/src/map/svg/parseInteractiveZonesSvg";
import { useAuthenticated, useColleagues } from "@gettactic/hooks";
import { useMap } from "@/features/map/MapProvider";
import { MapViewSvgInteractiveZones } from "@/features/map/components/MapView/MapViewSvg/MapViewSvgInteractiveZones";
import { useIsMultiRoomSelectionEnabled } from "@/features/reserve/hooks/useIsMultiRoomSelectionEnabled";
import { useRoomSelection } from "@/features/reserve/providers/roomSelectionContext";
import React from "react";

export interface MapInteractiveZonesProps {
  svgRef: React.RefObject<SVGSVGElement | null | undefined>;
  interactionsDisabled?: boolean;
  isMapBuilder?: boolean;
  onReady?: () => void;
}

export const MapInteractiveZones: React.FC<MapInteractiveZonesProps> = ({
  svgRef,
  interactionsDisabled = false,
  isMapBuilder = false,
  onReady
}) => {
  const {
    userContext: { authenticatedUser, currentOffice }
  } = useAuthenticated();

  const {
    schedulable,
    resources,
    startDate,
    selectedResource,
    selectResource,
    events,
    zonesInfo,
    setZonesInfo,
    startReserve
  } = useMap();

  const { colleagues } = useColleagues(authenticatedUser?.user?.id || "");
  const temporarySingleMapClick = isTemporarySkipMapClickOrg(authenticatedUser);

  const isMultiRoomSelectionEnabled = useIsMultiRoomSelectionEnabled();
  const { rooms: selectedRooms, isPickingRoom, addRoom } = useRoomSelection();
  const isPickingEnabled = isMultiRoomSelectionEnabled && isPickingRoom;

  const schedulableObject = React.useMemo(() => {
    return schedulable?.reduce(
      (acc, res) => {
        acc[res.id] = res;
        return acc;
      },
      {} as Record<string, IResource>
    );
  }, [schedulable]);

  const highlightCircleRef = React.useRef<SVGSVGElement | null>(null);
  const focusCircleRef = React.useRef<SVGSVGElement | null>(null);
  const interactiveRef = React.useRef<SVGSVGElement | null>(null);

  const lastAccessedDeskResourceId =
    authenticatedUser?.user?.organization_user
      ?.last_accessed_desk_resource_id ?? null;
  const lastAccessedRoomResourceId =
    authenticatedUser?.user?.organization_user
      ?.last_accessed_meeting_room_resource_id ?? null;

  /** While picking, a room click adds it to the reservation instead of opening it. */
  const addPickedRoom = (resId: string) => {
    const res = schedulable?.find((x) => x.id === resId);
    if (res?.type !== "meeting_room") return;
    addRoom(res);
  };

  const handleSelectResource = (resId: string) => {
    if (interactionsDisabled) return;
    if (isPickingEnabled) {
      addPickedRoom(resId);
      return;
    }
    selectResource(resId, false, () => {
      // Map-builder zones are data-driven and stable — skip DOM re-parse
      if (isMapBuilder) return;
      setZonesInfo(
        parseInteractiveZonesSvg(svgRef.current!) || {
          mapInfo: null,
          zones: []
        }
      );
    });
  };

  return (
    <MapViewSvgInteractiveZones
      startHidden
      ref={(ref) => {
        interactiveRef.current = ref;
        if (!ref || !svgRef.current) return;
        const svgEl = svgRef.current;
        ref.style.width =
          svgEl.style.width || `${svgEl.getAttribute("width") ?? 0}px`;
        ref.style.height =
          svgEl.style.height || `${svgEl.getAttribute("height") ?? 0}px`;
        ref.style.position = "absolute";
        ref.style.top = "0";
        ref.style.left = "0";
        setTimeout(() => {
          ref.style.opacity = "1";

          onReady?.();
        }, 50);
      }}
      zonesInfo={zonesInfo}
      resources={schedulable || []}
      totalResources={resources || []}
      resourceSelected={
        !temporarySingleMapClick || isPickingEnabled
          ? handleSelectResource
          : undefined
      }
      isPickingRoom={isPickingEnabled}
      selectedRoomIds={
        isMultiRoomSelectionEnabled
          ? selectedRooms.map((room) => room.id)
          : undefined
      }
      focusedCircleRef={focusCircleRef}
      highlightCircleRef={highlightCircleRef}
      highlightResource={(resId) => {
        if (interactionsDisabled) return;
        events.trigger(
          "onHighlightResource",
          resId ? schedulableObject?.[resId] || null : null
        );
      }}
      openReservation={startReserve}
      selectedResource={selectedResource}
      lastAccessedDeskResourceId={lastAccessedDeskResourceId}
      lastAccessedRoomResourceId={lastAccessedRoomResourceId}
      authenticatedUser={authenticatedUser}
      officeId={currentOffice?.id}
      startDate={startDate}
      colleagues={colleagues?.elements.map((colleague) => colleague.id) || []}
    />
  );
};
